import type { BrregHit } from "@/hooks/use-brreg-search";
import { formatDate } from "@/lib/format";

/** Selskapet slik create_manual_lead tar det imot (samme felter som importen fyller ut). */
export interface ManualLeadCompany {
  organization_number: string;
  name: string;
  industry_code: string | null;
  industry_description: string | null;
  employees: number | null;
  municipality: string | null;
  website: string | null;
  address: { adresse: string | null; postnummer: string | null; poststed: string | null };
}

/** Svaret fra check_lead_duplicate. Kunde-id og ansvarlige er null for møtebookere. */
export type LeadDuplicate =
  | { kind: "customer"; name: string; customer_id: string | null; owners: string[] | null }
  | {
      kind: "lead";
      name: string;
      list_name: string | null;
      status: string;
      attempts_count: number;
      last_attempt_at: string | null;
    };

const LEAD_STATUS_NB: Record<string, string> = {
  open: "Åpent",
  locked: "Noen ringer det nå",
  in_pipe: "I pipe",
  customer: "Ble kunde",
  parked: "Parkert",
  irrelevant: "Avvist",
};

/** «BÆRUM» blir «Bærum», «NORDRE FOLLO» blir «Nordre Follo». */
function titleCase(s: string | null | undefined): string | null {
  const t = s?.trim();
  if (!t) return null;
  return t.toLocaleLowerCase("nb").replace(/(^|[\s-])(\p{L})/gu, (_, sep: string, c: string) => sep + c.toLocaleUpperCase("nb"));
}

export function companyFromBrregHit(hit: BrregHit): ManualLeadCompany {
  return {
    organization_number: hit.organisasjonsnummer,
    name: hit.navn,
    industry_code: hit.naeringskode ?? null,
    industry_description: hit.naeringsbeskrivelse ?? null,
    employees: hit.antall_ansatte ?? null,
    municipality: titleCase(hit.kommune),
    website: hit.hjemmeside,
    address: { adresse: hit.adresse, postnummer: hit.postnummer, poststed: hit.poststed },
  };
}

/** Linjen under overskriften i advarselen om at selskapet finnes fra før. */
export function duplicateDetail(dup: LeadDuplicate): string {
  if (dup.kind === "customer") {
    const owners = dup.owners ?? [];
    return owners.length > 0
      ? `Kunden eies av ${owners.join(" og ")}. Ta kontakt i stedet for å ringe kaldt.`
      : "Ta kontakt med kundeansvarlig i stedet for å ringe kaldt.";
  }
  const parts = [
    dup.list_name ? `Liste: ${dup.list_name}` : null,
    LEAD_STATUS_NB[dup.status] ?? dup.status,
    dup.attempts_count > 0
      ? `ringt ${dup.attempts_count} ${dup.attempts_count === 1 ? "gang" : "ganger"}${dup.last_attempt_at ? `, sist ${formatDate(dup.last_attempt_at)}` : ""}`
      : "ikke ringt ennå",
  ];
  return parts.filter(Boolean).join(" · ");
}

export function duplicateTitle(dup: LeadDuplicate): string {
  return dup.kind === "customer" ? `${dup.name} er allerede kunde.` : `${dup.name} finnes allerede som lead.`;
}
